import {
  IonButtons,
  IonContent,
  IonHeader,
  IonLoading,
  IonMenuButton,
  IonPage,
  IonTitle,
  IonToast,
  IonToolbar,
  useIonViewWillEnter,
} from "@ionic/react";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Alert,
  Stack,
  Button,
  IconButton,
  Tooltip,
  alpha,
  useTheme,
  Paper,
  Fade,
  CircularProgress,
  Chip,
  InputAdornment,
  Avatar,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import {
  AttachMoney,
  AccountBalance,
  Receipt,
  Description,
  Assignment,
  CalendarToday,
  Send,
  Refresh,
  CheckCircle,
  Error,
  Info,
  Print,
  CloudUpload,
  TrendingUp,
  TrendingDown,
  Event,
} from "@mui/icons-material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import axios from "axios";
import { requestID, baseUrl, config } from "../../common/utils";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useBusinessContext } from "../../context/business-context";
import { MenuButton } from "../../components/Menu";

const LedgerPosting_sales_only = () => {
  const theme = useTheme();
  const { name, transType } = useParams<{ name: string; transType?: string }>();
  
  // Determine default transaction type from route param
  const getDefaultTransactionType = () => {
    if (transType?.toUpperCase() === 'DR' || transType?.toUpperCase() === 'DEBIT') {
      return 'DR';
    }
    if (transType?.toUpperCase() === 'CR' || transType?.toUpperCase() === 'CREDIT') {
      return 'CR';
    }
    return "";
  };
  
  const schema = yup
    .object({
      ledger: yup.string().required("Please select a ledger"),
      transactionType: yup.string()
        .required("Please select transaction type")
        .oneOf(['DR', 'CR'], 'Transaction type must be either DR or CR'),
      amount: yup.number()
        .required("Please enter an amount")
        .positive("Amount must be positive")
        .typeError("Please enter a valid number"),
      desp: yup.string()
        .required("Please enter transaction description")
        .min(10, "Description should be at least 10 characters"),
      reference: yup.string().required("Please select a reference type"),
      referenceDetail: yup.string()
        .required("Please enter reference details")
        .min(3, "Reference details should be at least 3 characters"),
      postingDate: yup
        .date()
        .required("Please select posting date")
        .typeError("Please select a valid date")
        .max(dayjs().add(1, 'day').toDate(), "Posting date cannot be in the future")
        .min(dayjs().subtract(1, 'year').toDate(), "Posting date cannot be older than 1 year"),
    })
    .required();

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
    reset,
    watch,
    setValue,
  } = useForm({
    resolver: yupResolver(schema),
    mode: 'onChange',
    defaultValues: {
      ledger: "",
      transactionType: getDefaultTransactionType(),
      amount: "",
      desp: "",
      reference: "",
      referenceDetail: "",
      postingDate: dayjs().toDate(), // Set today's date as default
    },
  });

  const [status, setStatus] = useState({ loading: false, error: false });
  const [errorLogin, setErrorLogin] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [recData, setRecData] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [drName, setDrname] = useState("");
  const [crName, setCrname] = useState("");
  const [allLedger, setAllLedger] = useState([]);
  const [checkTeller, setcheckTeller] = useState(false);
  const [appSettings, setAppsettings] = useState([]);

  const { selectedBusiness } = useBusinessContext();

  const getLedgers = () => {
    const formData = new FormData();
    formData.append("requestID", requestID());
    
    setStatus({ loading: true, error: false });
    axios
      .post(baseUrl() + "ledgercreates/getAllLedger_caas_branch_sales_only", formData, config)
      .then((response) => {
        setAllLedger(response.data.data || []);
        setStatus({ loading: false, error: false });
      })
      .catch((error) => {
        console.error("Error fetching ledgers:", error);
        setStatus({ loading: false, error: false });
      });
  };

  const postLedger = async (data) => {
    setStatus({ loading: true, error: false });
    setShowSuccess(false);
    
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("amount", data.amount);
      formData.append("post_sn", data.ledger);
      formData.append("transType", data.transactionType);
      formData.append("desp", data.desp);
      formData.append("reference", data.reference);
      formData.append("referenceDetail", data.referenceDetail);
      formData.append("checkPrint", checkTeller.toString());
      
      // Add posting date to the form data
      // Format the date as YYYY-MM-DD for backend
      const postingDate = dayjs(data.postingDate).format('YYYY-MM-DD');
      formData.append("postingDate", postingDate);

      const response = await axios.post(
        baseUrl() + "ledgers/postLedgerAccount_caas_sales_only",
        formData,
        config
      );
      console.log("Ledger posting response:", response.data);

      if (response.data.status === true) {
        setAppsettings(response.data.appSettings || {});
        setRecData(response.data.data || []);
        setDrname(response.data.drName || "");
        setCrname(response.data.crName || "");

        // Reset form but keep transaction type from route param and today's date
        reset({
          ledger: "",
          transactionType: getDefaultTransactionType(),
          amount: "",
          desp: "",
          reference: "",
          referenceDetail: "",
          postingDate: dayjs().toDate(), // Reset to today's date
        });
        setcheckTeller(false);
        setShowSuccess(true);
        
        if (response.data.checkPrint === "true") {
          setIsOpen(true);
        }
      }

      setErrorLogin(response.data.status === "false" ? "Transaction failed!" : "Ledger Posted Successfully!");
      setShowToast(true);
      setStatus({ loading: false, error: false });
    } catch (error) {
      console.error("Error posting ledger:", error);
      setStatus({ loading: false, error: true });
      setErrorLogin("Error posting ledger. Please try again.");
      setShowToast(true);
    }
  };

  useIonViewWillEnter(() => {
    getLedgers();
  });

  useEffect(() => {
    const defaultType = getDefaultTransactionType();
    if (defaultType) {
      setValue("transactionType", defaultType);
    }
  }, [transType, setValue]);

  const referenceOptions = [
    { value: "depoNo", label: "DEPOSIT SLIP NO.", icon: "💰" },
    { value: "chequeNo", label: "CHEQUE NO.", icon: "🏦" },
    { value: "posNo", label: "POS TERMINAL NO.", icon: "💳" },
    { value: "pvNo", label: "PAYMENT VOUCHER NO.", icon: "📄" },
    { value: "trfNo", label: "TRANSFER REFERENCE", icon: "↔️" },
    { value: "recNo", label: "RECEIPT NO.", icon: "🧾" },
  ];

  const transactionTypeOptions = [
    { 
      value: "DR", 
      label: "Debit (DR)", 
      description: "Increase asset/expense, Decrease liability/equity/revenue",
      color: "error", // RED for DR (Changed from success)
      icon: <TrendingUp />,
      shortLabel: "Debit"
    },
    { 
      value: "CR", 
      label: "Credit (CR)", 
      description: "Decrease asset/expense, Increase liability/equity/revenue",
      color: "success", // GREEN for CR (Changed from error)
      icon: <TrendingDown />,
      shortLabel: "Credit"
    }
  ];

  const getPageTitle = () => {
    if (transType?.toUpperCase() === 'DR' || transType?.toUpperCase() === 'DEBIT') {
      return "Debit Ledger Posting";
    }
    if (transType?.toUpperCase() === 'CR' || transType?.toUpperCase() === 'CREDIT') {
      return "Credit Ledger Posting";
    }
    return "Ledger Posting";
  };

  const getPageSubtitle = () => {
    if (transType?.toUpperCase() === 'DR' || transType?.toUpperCase() === 'DEBIT') {
      return "Record debit transactions to increase assets/expenses or decrease liabilities/equity/revenue";
    }
    if (transType?.toUpperCase() === 'CR' || transType?.toUpperCase() === 'CREDIT') {
      return "Record credit transactions to decrease assets/expenses or increase liabilities/equity/revenue";
    }
    return "Record new financial transactions to the ledger system";
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const selectedLedger = watch("ledger");
  const selectedTransactionType = watch("transactionType");
  const selectedPostingDate = watch("postingDate");
  const selectedLedgerName = allLedger.find(l => l.sn === selectedLedger)?.name || "";

  const getTransactionTypeInfo = (type) => {
    const option = transactionTypeOptions.find(opt => opt.value === type);
    return option || transactionTypeOptions[0];
  };

  // Common styles for equal height form controls
  const equalHeightStyles = {
    '& .MuiInputBase-root': {
      height: '56px', // Standard Material-UI height
      display: 'flex',
      alignItems: 'center',
    },
    '& .MuiSelect-select': {
      display: 'flex',
      alignItems: 'center',
      height: 'auto !important',
      minHeight: '24px',
    },
    '& .MuiOutlinedInput-input': {
      padding: '16.5px 14px',
    }
  };

  return (
    <IonPage className="pushDown">
      <MenuButton/>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <IonContent fullscreen>
          <Box sx={{ p: { xs: 2, md: 3 } }}>
            {/* Header Section */}
            <Box sx={{ mb: 3 }}>
              <Stack spacing={1} sx={{ mb: 2 }}>
                <Typography variant="h4" component="h1" fontWeight="700" 
                  color={selectedTransactionType === 'DR' ? 'error' : selectedTransactionType === 'CR' ? 'success' : 'primary'}
                >
                  {getPageTitle()}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {getPageSubtitle()}
                </Typography>
              </Stack>
              
              {/* Transaction Info Card */}
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 2, 
                  borderRadius: 2,
                  background: selectedTransactionType === 'DR' 
                    ? alpha(theme.palette.error.main, 0.05)
                    : selectedTransactionType === 'CR'
                      ? alpha(theme.palette.success.main, 0.05)
                      : alpha(theme.palette.primary.main, 0.05),
                  border: `1px solid ${
                    selectedTransactionType === 'DR' 
                      ? alpha(theme.palette.error.main, 0.1)
                      : selectedTransactionType === 'CR'
                        ? alpha(theme.palette.success.main, 0.1)
                        : alpha(theme.palette.primary.main, 0.1)
                  }`,
                }}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar sx={{ 
                    bgcolor: selectedTransactionType === 'DR' 
                      ? theme.palette.error.main
                      : selectedTransactionType === 'CR'
                        ? theme.palette.success.main
                        : theme.palette.primary.main
                  }}>
                    {selectedTransactionType === 'DR' ? <TrendingUp /> : 
                     selectedTransactionType === 'CR' ? <TrendingDown /> : 
                     <Event />}
                  </Avatar>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Posting Date
                    </Typography>
                    <Typography variant="h6" fontWeight="600">
                      {selectedPostingDate ? dayjs(selectedPostingDate).format('dddd, MMMM D, YYYY') : 'Select posting date'}
                    </Typography>
                  </Box>
                  {selectedLedger && (
                    <Box sx={{ ml: 'auto' }}>
                      <Chip 
                        label={`${selectedLedgerName}`}
                        color="primary"
                        variant="outlined"
                        sx={{ mr: 1 }}
                      />
                      {selectedTransactionType && (
                        <Chip 
                          label={getTransactionTypeInfo(selectedTransactionType).shortLabel}
                          color={selectedTransactionType === 'DR' ? 'error' : 'success'}
                          variant="filled"
                          icon={selectedTransactionType === 'DR' ? <TrendingUp /> : <TrendingDown />}
                        />
                      )}
                    </Box>
                  )}
                </Stack>
              </Paper>
            </Box>

            {/* Success Message */}
            <Fade in={showSuccess}>
              <Alert 
                severity="success" 
                icon={<CheckCircle />}
                sx={{ mb: 3, borderRadius: 2 }}
                action={
                  <IconButton
                    aria-label="close"
                    color="inherit"
                    size="small"
                    onClick={() => setShowSuccess(false)}
                  >
                    <Error fontSize="inherit" />
                  </IconButton>
                }
              >
                <Typography variant="body1" fontWeight="500">
                  Transaction posted successfully!
                </Typography>
                <Typography variant="body2">
                  The {selectedTransactionType === 'DR' ? 'debit' : 'credit'} ledger entry has been recorded and saved.
                </Typography>
              </Alert>
            </Fade>

            {/* Main Form Card */}
            <Card 
              elevation={2} 
              sx={{ 
                mb: 4, 
                borderRadius: 3,
                overflow: 'hidden',
                border: `1px solid ${
                  selectedTransactionType === 'DR' 
                    ? theme.palette.error.light // RED border for DR
                    : selectedTransactionType === 'CR'
                      ? theme.palette.success.light // GREEN border for CR
                      : theme.palette.divider
                }`,
              }}
            >
              <CardHeader
                title={
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar sx={{ 
                      bgcolor: selectedTransactionType === 'DR' 
                        ? theme.palette.error.main // RED for DR
                        : selectedTransactionType === 'CR'
                          ? theme.palette.success.main // GREEN for CR
                          : theme.palette.primary.main
                    }}>
                      {selectedTransactionType === 'DR' ? <TrendingUp /> : 
                       selectedTransactionType === 'CR' ? <TrendingDown /> : 
                       <AccountBalance />}
                    </Avatar>
                    <Box>
                      <Typography variant="h5" fontWeight="600">
                        {selectedTransactionType === 'DR' ? 'Debit Transaction Details' :
                         selectedTransactionType === 'CR' ? 'Credit Transaction Details' :
                         'Transaction Details'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedTransactionType 
                          ? `Enter ${selectedTransactionType === 'DR' ? 'debit' : 'credit'} transaction information below`
                          : 'Enter all required information below'}
                      </Typography>
                    </Box>
                  </Stack>
                }
                sx={{ 
                  bgcolor: selectedTransactionType === 'DR' 
                    ? alpha(theme.palette.error.main, 0.02) // Light RED for DR
                    : selectedTransactionType === 'CR'
                      ? alpha(theme.palette.success.main, 0.02) // Light GREEN for CR
                      : alpha(theme.palette.primary.main, 0.02),
                  borderBottom: `1px solid ${
                    selectedTransactionType === 'DR' 
                      ? alpha(theme.palette.error.main, 0.1) // RED border for DR
                      : selectedTransactionType === 'CR'
                        ? alpha(theme.palette.success.main, 0.1) // GREEN border for CR
                        : theme.palette.divider
                  }`,
                }}
              />
              <Divider />
              <CardContent sx={{ p: { xs: 2, md: 4 } }}>
                <form onSubmit={handleSubmit(postLedger)}>
                  <Grid container spacing={4}>
                    {/* Posting Date Field - Equal Height */}
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="postingDate"
                        control={control}
                        render={({ field: { onChange, value, ...field } }) => (
                          <DatePicker
                            label="Posting Date"
                            value={value ? dayjs(value) : null}
                            onChange={(date) => onChange(date ? date.toDate() : null)}
                            disabled={status.loading}
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                variant: "outlined",
                                error: !!errors.postingDate,
                                helperText: errors.postingDate?.message,
                                sx: equalHeightStyles,
                                InputProps: {
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <Event sx={{ color: 'action.active' }} />
                                    </InputAdornment>
                                  ),
                                  sx: { 
                                    height: '56px',
                                    '& input': {
                                      height: '100%',
                                      padding: '16.5px 14px',
                                    }
                                  }
                                }
                              }
                            }}
                          />
                        )}
                      />
                    </Grid>

                    {/* Ledger Selection - Equal Height */}
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth size="medium" sx={equalHeightStyles}>
                        <InputLabel id="ledger-select-label" sx={{ fontWeight: 500 }}>
                          Select Ledger Account
                        </InputLabel>
                        <Controller
                          name="ledger"
                          control={control}
                          render={({ field }) => (
                            <Select
                              {...field}
                              labelId="ledger-select-label"
                              label="Select Ledger Account"
                              error={!!errors.ledger}
                              disabled={status.loading}
                              startAdornment={<AccountBalance sx={{ mr: 1, color: 'action.active' }} />}
                              sx={{ 
                                height: '56px',
                                '& .MuiSelect-select': { 
                                  display: 'flex', 
                                  alignItems: 'center',
                                  minHeight: '24px',
                                  py: 1.5,
                                }
                              }}
                            >
                              <MenuItem value="">
                                <Typography color="text.secondary" fontStyle="italic">
                                  Select a ledger account...
                                </Typography>
                              </MenuItem>
                              {allLedger.map((ledger, index) => (
                                <MenuItem key={index} value={ledger.sn}>
                                  <Stack direction="row" spacing={2} alignItems="center">
                                    <Avatar sx={{ 
                                      width: 32, 
                                      height: 32, 
                                      bgcolor: selectedTransactionType === 'DR' 
                                        ? alpha(theme.palette.error.main, 0.1) // RED tint for DR
                                        : selectedTransactionType === 'CR'
                                          ? alpha(theme.palette.success.main, 0.1) // GREEN tint for CR
                                          : alpha(theme.palette.primary.main, 0.1),
                                      color: selectedTransactionType === 'DR' 
                                        ? theme.palette.error.main // RED text for DR
                                        : selectedTransactionType === 'CR'
                                          ? theme.palette.success.main // GREEN text for CR
                                          : theme.palette.primary.main,
                                      fontSize: 14,
                                    }}>
                                      {ledger.name.charAt(0)}
                                    </Avatar>
                                    <Box>
                                      <Typography variant="body1">{ledger.name}</Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        Account Code: {ledger.sn}
                                      </Typography>
                                    </Box>
                                  </Stack>
                                </MenuItem>
                              ))}
                            </Select>
                          )}
                        />
                        {errors.ledger && (
                          <Typography color="error" variant="caption" sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
                            <Error sx={{ fontSize: 16, mr: 0.5 }} /> {errors.ledger.message}
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>

                    {/* Transaction Type Selector - Equal Height */}
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth size="medium" sx={equalHeightStyles}>
                        <InputLabel id="transaction-type-label" sx={{ fontWeight: 500 }}>
                          Transaction Type
                        </InputLabel>
                        <Controller
                          name="transactionType"
                          control={control}
                          render={({ field }) => (
                            <Select
                              {...field}
                              labelId="transaction-type-label"
                              label="Transaction Type"
                              error={!!errors.transactionType}
                              disabled={status.loading}
                              sx={{ 
                                height: '56px',
                                '& .MuiSelect-select': {
                                  display: 'flex',
                                  alignItems: 'center',
                                  minHeight: '24px',
                                  py: 1.5,
                                }
                              }}
                            >
                              <MenuItem value="">
                                <Typography color="text.secondary" fontStyle="italic">
                                  Select transaction type...
                                </Typography>
                              </MenuItem>
                              {transactionTypeOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  <Stack direction="row" spacing={2} alignItems="center">
                                    <Box sx={{ 
                                      display: 'flex', 
                                      alignItems: 'center', 
                                      justifyContent: 'center',
                                      width: 40,
                                      height: 40,
                                      borderRadius: 1,
                                      bgcolor: option.color === 'error' 
                                        ? alpha(theme.palette.error.main, 0.1) // RED background for DR
                                        : alpha(theme.palette.success.main, 0.1), // GREEN background for CR
                                      color: option.color === 'error' 
                                        ? theme.palette.error.main // RED text for DR
                                        : theme.palette.success.main, // GREEN text for CR
                                    }}>
                                      {option.icon}
                                    </Box>
                                    <Box>
                                      <Typography variant="body1" fontWeight={500}>
                                        {option.label}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 300 }}>
                                        {option.description}
                                      </Typography>
                                    </Box>
                                  </Stack>
                                </MenuItem>
                              ))}
                            </Select>
                          )}
                        />
                        {errors.transactionType && (
                          <Typography color="error" variant="caption" sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
                            <Error sx={{ fontSize: 16, mr: 0.5 }} /> {errors.transactionType.message}
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>

                    {/* Amount Field - Equal Height */}
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="amount"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            type="number"
                            label="Transaction Amount"
                            variant="outlined"
                            error={!!errors.amount}
                            helperText={errors.amount?.message}
                            disabled={status.loading}
                            sx={equalHeightStyles}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <AttachMoney sx={{ 
                                    color: selectedTransactionType === 'DR' 
                                      ? theme.palette.error.main // RED for DR
                                      : selectedTransactionType === 'CR'
                                        ? theme.palette.success.main // GREEN for CR
                                        : theme.palette.error.main
                                  }} />
                                </InputAdornment>
                              ),
                              sx: { 
                                height: '56px',
                                '& input': {
                                  height: '100%',
                                  padding: '16.5px 14px',
                                }
                              }
                            }}
                            slotProps={{
                              htmlInput: {
                                step: "0.01",
                                min: "0"
                              }
                            }}
                          />
                        )}
                      />
                    </Grid>

                    {/* Description Field */}
                    <Grid item xs={12}>
                      <Controller
                        name="desp"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Transaction Description"
                            variant="outlined"
                            error={!!errors.desp}
                            helperText={errors.desp?.message || "Provide a clear description of this transaction"}
                            disabled={status.loading}
                            multiline
                            rows={3}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Description sx={{ color: 'action.active', mt: '28px' }} />
                                </InputAdornment>
                              ),
                            }}
                          />
                        )}
                      />
                    </Grid>

                    {/* Reference Type - Equal Height */}
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth size="medium" sx={equalHeightStyles}>
                        <InputLabel id="reference-select-label" sx={{ fontWeight: 500 }}>
                          Reference Type
                        </InputLabel>
                        <Controller
                          name="reference"
                          control={control}
                          render={({ field }) => (
                            <Select
                              {...field}
                              labelId="reference-select-label"
                              label="Reference Type"
                              error={!!errors.reference}
                              disabled={status.loading}
                              startAdornment={<Assignment sx={{ mr: 1, color: 'action.active' }} />}
                              sx={{ 
                                height: '56px',
                                '& .MuiSelect-select': {
                                  display: 'flex',
                                  alignItems: 'center',
                                  minHeight: '24px',
                                  py: 1.5,
                                }
                              }}
                            >
                              <MenuItem value="">
                                <Typography color="text.secondary" fontStyle="italic">
                                  Select reference type...
                                </Typography>
                              </MenuItem>
                              {referenceOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  <Stack direction="row" spacing={2} alignItems="center">
                                    <Typography variant="h6">{option.icon}</Typography>
                                    <Typography>{option.label}</Typography>
                                  </Stack>
                                </MenuItem>
                              ))}
                            </Select>
                          )}
                        />
                        {errors.reference && (
                          <Typography color="error" variant="caption" sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
                            <Error sx={{ fontSize: 16, mr: 0.5 }} /> {errors.reference.message}
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>

                    {/* Reference Detail - Equal Height */}
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="referenceDetail"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Reference Details"
                            variant="outlined"
                            error={!!errors.referenceDetail}
                            helperText={errors.referenceDetail?.message || "Enter specific reference number or details"}
                            disabled={status.loading}
                            sx={equalHeightStyles}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Receipt sx={{ color: 'action.active' }} />
                                </InputAdornment>
                              ),
                              sx: { 
                                height: '56px',
                                '& input': {
                                  height: '100%',
                                  padding: '16.5px 14px',
                                }
                              }
                            }}
                          />
                        )}
                      />
                    </Grid>

                    {/* Action Section */}
                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                      <Stack 
                        direction={{ xs: 'column', md: 'row' }} 
                        spacing={3} 
                        justifyContent="space-between" 
                        alignItems={{ xs: 'stretch', md: 'center' }}
                        sx={{ pt: 2 }}
                      >
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Button
                            variant="outlined"
                            onClick={() => reset({
                              ledger: "",
                              transactionType: getDefaultTransactionType(),
                              amount: "",
                              desp: "",
                              reference: "",
                              referenceDetail: "",
                              postingDate: dayjs().toDate(), // Reset to today's date
                            })}
                            disabled={status.loading || isSubmitting}
                            startIcon={<Refresh />}
                            size="large"
                            sx={{ minWidth: 140 }}
                          >
                            Clear Form
                          </Button>
                          
                          <Button
                            type="submit"
                            variant="contained"
                            color={selectedTransactionType === 'DR' ? 'error' : // RED for DR
                                   selectedTransactionType === 'CR' ? 'success' : // GREEN for CR
                                   'primary'}
                            disabled={status.loading || isSubmitting || !isValid}
                            startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <Send />}
                            size="large"
                            sx={{ 
                              minWidth: 180,
                              background: selectedTransactionType === 'DR' 
                                ? `linear-gradient(135deg, ${theme.palette.error.main} 0%, ${theme.palette.error.dark} 100%)` // RED gradient for DR
                                : selectedTransactionType === 'CR'
                                  ? `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)` // GREEN gradient for CR
                                  : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                              '&:hover': {
                                background: selectedTransactionType === 'DR' 
                                  ? `linear-gradient(135deg, ${theme.palette.error.dark} 0%, ${theme.palette.error.main} 100%)` // RED hover for DR
                                  : selectedTransactionType === 'CR'
                                    ? `linear-gradient(135deg, ${theme.palette.success.dark} 0%, ${theme.palette.success.main} 100%)` // GREEN hover for CR
                                    : `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                              }
                            }}
                          >
                            {isSubmitting ? "Processing..." : `Post ${selectedTransactionType === 'DR' ? 'Debit' : selectedTransactionType === 'CR' ? 'Credit' : ''} Transaction`}
                          </Button>
                        </Stack>
                      </Stack>
                    </Grid>
                  </Grid>
                </form>
              </CardContent>
            </Card>

            {/* Info Alert */}
            <Alert 
              severity="info" 
              icon={<Info />}
              sx={{ borderRadius: 2 }}
            >
              <Typography variant="body2">
                <strong>Transaction Type Guide:</strong><br />
                • <strong style={{ color: theme.palette.error.main }}>Debit (DR):</strong> Records an increase in assets or expenses, or a decrease in liabilities, equity, or revenue.<br />
                • <strong style={{ color: theme.palette.success.main }}>Credit (CR):</strong> Records a decrease in assets or expenses, or an increase in liabilities, equity, or revenue.<br />
                <strong>Posting Date:</strong> Select the date when this transaction should be recorded. By default, it's set to today's date.<br />
                All fields are required. Ensure the transaction details are accurate before posting.
              </Typography>
            </Alert>
          </Box>

          <IonLoading
            message="Processing transaction..."
            spinner="circles"
            isOpen={status.loading || isSubmitting}
          />
          <IonToast 
            position="middle" 
            isOpen={showToast} 
            message={errorLogin} 
            duration={3000}
            onDidDismiss={() => setShowToast(false)}
          />
        </IonContent>
      </LocalizationProvider>
    </IonPage>
  );
};

export default LedgerPosting_sales_only;